import type { UUID } from '@cmg/common';
import type { IntlShape } from '@cmg/i18n';

import {
  InstitutionalIndicationOrderType,
  InterestLevelInterestUnit,
  InterestLevelLimitType,
  SecurityType,
} from '../../../../../../../../graphql';
import {
  interestUnitLabels,
  limitTypeLabels,
} from '../../../../../../../../types/domain/order-book/constants';
import type { IndicationForm_IndicationDetails_OfferingPartsFragment } from '../../../indication-form/components/indication-details/graphql/__generated__';
import type { IndicationForm_MyIndicationPartsFragment } from '../../../indication-form/graphql/__generated__';

type GetInterestQuantityCellValueArgs = {
  interestUnit: InterestLevelInterestUnit;
  interestQuantity: number;
  demandCurrency?: string | null;
  intl: IntlShape;
};

export const getInterestQuantityCellValue = ({
  interestUnit,
  interestQuantity,
  demandCurrency,
  intl,
}: GetInterestQuantityCellValueArgs) => {
  switch (interestUnit) {
    case InterestLevelInterestUnit.Currency:
      return intl.formatNumber(interestQuantity, {
        format: 'currency',
        currency: demandCurrency ?? 'USD',
      });
    case InterestLevelInterestUnit.Percent:
      return intl.formatNumber(interestQuantity, { format: 'percent' });
    case InterestLevelInterestUnit.Shares:
    case InterestLevelInterestUnit.Bonds:
      return intl.formatNumber(interestQuantity, { format: 'integer' });
  }
};

type GetLimitTypeCellValueArgs = {
  orderType: InstitutionalIndicationOrderType;
  limitType: InterestLevelLimitType | null | undefined;
  currency: string | null | undefined;
  limitPrice: number | null | undefined;
  intl: IntlShape;
};
export const getLimitTypeCellValue = ({
  orderType,
  limitType,
  currency,
  limitPrice,
  intl,
}: GetLimitTypeCellValueArgs) => {
  if (orderType === InstitutionalIndicationOrderType.Market) {
    return;
  }
  const isLimitTypePrice = limitType === InterestLevelLimitType.Price;

  const formattedCurrency = isLimitTypePrice ? ` (${currency})` : '';
  const formattedLimitPrice = isLimitTypePrice
    ? intl.formatNumber(limitPrice, { format: 'currency', currency: currency ?? 'USD' })
    : intl.formatNumber(limitPrice, { format: 'percent' });

  return `${limitTypeLabels[limitType!]}${formattedCurrency}: ${formattedLimitPrice}`;
};

export const getFirms = (
  acknowledgements: IndicationForm_MyIndicationPartsFragment['acknowledgements'],
  managers: IndicationForm_IndicationDetails_OfferingPartsFragment['syndicate']['managers'],
  matchingCmgEntityKeys: readonly UUID[],
  indicationVersion: string
) => {
  const firms = acknowledgements.filter(
    ({ acknowledgedIndicationVersion, managerCmgEntityKey }) =>
      managers.some(({ cmgEntityKey }) => cmgEntityKey === managerCmgEntityKey) &&
      (matchingCmgEntityKeys.includes(managerCmgEntityKey) ||
        acknowledgedIndicationVersion === indicationVersion)
  );
  return firms.map(({ managerCmgEntityKey, acknowledgedIndicationVersion }) => ({
    firmName:
      managers?.find(({ cmgEntityKey }) => managerCmgEntityKey === cmgEntityKey)?.firmName ?? null,
    isRevised: acknowledgedIndicationVersion !== indicationVersion,
    isUsersFirm: matchingCmgEntityKeys.includes(managerCmgEntityKey),
  }));
};

export const ACK_BY_TOOLTIP_TEXT =
  'Acknowledgement of an indication of interest (IOI) is not a sale, an acceptance of an order, a trade confirmation or a commitment to make an allocation. It merely demonstrates receipt of such IOI.';

export function getDemandUnitLabel(
  interestUnit: InterestLevelInterestUnit,
  securityType: SecurityType | null,
  isDepositaryReceiptEnabled: boolean
) {
  if (
    securityType === SecurityType.CommonOrdinaryShare &&
    isDepositaryReceiptEnabled &&
    interestUnit === InterestLevelInterestUnit.Shares
  ) {
    return 'ADS/ADR';
  }

  return interestUnitLabels[interestUnit];
}
